import React, { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { IPushDailyStat } from '../../types/pushStats';
import { formatLongDate, formatShortDate, totalOf } from './pushStatsUtils';

export const usePushColors = () => {
  const dark = useTheme().palette.mode === 'dark';
  return {
    custom: dark ? '#64B5F6' : '#1565C0',
    coupon: dark ? '#FFC14D' : '#E8A317',
    failed: dark ? '#FF6E66' : '#C62828',
  };
};

interface Props {
  rows: IPushDailyStat[];
  hoverDate: string | null;
  selectedDate: string | null;
  onHover: (date: string | null) => void;
  onSelect: (date: string) => void;
}

const MARGIN = { t: 12, r: 8, b: 26, l: 40 };

const niceMax = (value: number): number => {
  if (value <= 0) return 100;
  const p = Math.pow(10, Math.floor(Math.log10(value)));
  const m = value / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
};

const topRounded = (x: number, y: number, w: number, h: number, radius: number): string => {
  if (h <= 0) return '';
  const r = Math.min(radius, h, w / 2);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

const fmt = (n: number) => n.toLocaleString('en-GB');

const PushStatsChart: React.FC<Props> = ({ rows, hoverDate, selectedDate, onHover, onSelect }) => {
  const theme = useTheme();
  const colors = usePushColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const height = isMobile ? 260 : 320;

  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const n = rows.length;
  const innerW = Math.max(0, width - MARGIN.l - MARGIN.r);
  const innerH = height - MARGIN.t - MARGIN.b;
  const band = n ? innerW / n : 0;
  const gap = n > 60 ? 1 : Math.max(2, band * 0.28);
  const barW = Math.max(1, band - gap);
  const max = niceMax(Math.max(0, ...rows.map(totalOf)));
  const y = (v: number) => MARGIN.t + innerH - (v / max) * innerH;
  const ticks = [0, 1, 2, 3, 4].map((i) => (max / 4) * i);

  const hoverIndex = hoverDate ? rows.findIndex((r) => r.date === hoverDate) : -1;
  const selectedIndex = selectedDate ? rows.findIndex((r) => r.date === selectedDate) : -1;
  const hovered = hoverIndex >= 0 ? rows[hoverIndex] : null;

  const indexAt = (clientX: number, svg: SVGSVGElement) => {
    const box = svg.getBoundingClientRect();
    return Math.min(n - 1, Math.max(0, Math.floor((clientX - box.left - MARGIN.l) / band)));
  };

  const labelEvery = Math.ceil(n / Math.max(2, Math.floor(innerW / 56)));
  const textFill = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const tooltipLeft = (() => {
    if (hoverIndex < 0) return 0;
    const cx = MARGIN.l + hoverIndex * band + band / 2;
    return cx + 12 + 190 > width ? Math.max(0, cx - 190 - 12) : cx + 12;
  })();

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${Math.max(width, 1)} ${height}`}
        role="img"
        aria-label="Stacked daily columns of custom and coupon pushes with failure markers"
        style={{ display: 'block', touchAction: 'pan-y' }}
      >
        {ticks.map((v, i) => (
          <g key={v}>
            <line x1={MARGIN.l} x2={width - MARGIN.r} y1={y(v)} y2={y(v)} stroke={gridColor} strokeWidth={1} opacity={i === 0 ? 1 : 0.6} />
            <text x={MARGIN.l - 6} y={y(v) + 4} textAnchor="end" fontSize={11} fill={textFill}>
              {v >= 1000 ? `${v / 1000}k` : v}
            </text>
          </g>
        ))}

        {hoverIndex >= 0 && (
          <rect x={MARGIN.l + hoverIndex * band} y={MARGIN.t} width={band} height={innerH} fill={gridColor} opacity={0.5} />
        )}

        {rows.map((row, i) => {
          const x = MARGIN.l + i * band + gap / 2;
          const total = totalOf(row);
          if (total === 0) {
            return (
              <g key={row.date}>
                <line x1={x} x2={x + barW} y1={y(0) - 1} y2={y(0) - 1} stroke={textFill} strokeWidth={2} strokeDasharray="3 2" />
                {barW > 8 && (
                  <text x={x + barW / 2} y={y(0) - 6} textAnchor="middle" fontSize={11} fill={textFill}>0</text>
                )}
              </g>
            );
          }
          const yCustom = y(row.customSent);
          const yTop = y(total);
          const failRate = row.customSent ? row.customFailed / row.customSent : 0;
          return (
            <g key={row.date}>
              <rect x={x} y={yCustom} width={barW} height={Math.max(0, y(0) - yCustom)} fill={colors.custom} />
              <path d={topRounded(x, yTop, barW, Math.max(0, yCustom - yTop - 1), Math.min(3, barW / 3))} fill={colors.coupon} />
              {row.customFailed > 0 && n <= 60 && (
                <line x1={x} x2={x + barW} y1={y(row.customFailed)} y2={y(row.customFailed)} stroke={colors.failed} strokeWidth={2} />
              )}
              {failRate > 0.05 && (
                <circle cx={x + barW / 2} cy={yTop - 8} r={4} fill={theme.palette.background.paper} stroke={colors.failed} strokeWidth={2} />
              )}
            </g>
          );
        })}

        {selectedIndex >= 0 && (
          <rect
            x={MARGIN.l + selectedIndex * band}
            y={MARGIN.t}
            width={band}
            height={innerH}
            fill="none"
            stroke={theme.palette.secondary.main}
            strokeWidth={2}
            rx={2}
            pointerEvents="none"
          />
        )}

        {rows.map((row, i) =>
          (n - 1 - i) % labelEvery === 0 ? (
            <text key={row.date} x={MARGIN.l + i * band + band / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={textFill}>
              {formatShortDate(row.date)}
            </text>
          ) : null
        )}

        {n > 0 && (
          <rect
            x={MARGIN.l}
            y={MARGIN.t}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={(e) => onHover(rows[indexAt(e.clientX, e.currentTarget.ownerSVGElement as SVGSVGElement)].date)}
            onMouseLeave={() => onHover(null)}
            onTouchStart={(e) => onHover(rows[indexAt(e.touches[0].clientX, e.currentTarget.ownerSVGElement as SVGSVGElement)].date)}
            onTouchMove={(e) => onHover(rows[indexAt(e.touches[0].clientX, e.currentTarget.ownerSVGElement as SVGSVGElement)].date)}
            onClick={(e) => onSelect(rows[indexAt(e.clientX, e.currentTarget.ownerSVGElement as SVGSVGElement)].date)}
          />
        )}
      </svg>

      {hovered && (
        <Box
          role="status"
          sx={{
            position: 'absolute',
            top: MARGIN.t,
            left: tooltipLeft,
            minWidth: 170,
            p: '8px 10px',
            borderRadius: 2,
            fontSize: 12,
            lineHeight: 1.5,
            pointerEvents: 'none',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900',
            color: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
            boxShadow: 4,
          }}
        >
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>{formatLongDate(hovered.date)}</Box>
          <TipRow color={colors.custom} label="Custom" value={fmt(hovered.customSent)} />
          <TipRow color={colors.coupon} label="Coupon*" value={fmt(hovered.couponSent)} />
          <TipRow label="Total" value={fmt(totalOf(hovered))} />
          <Box sx={{ borderTop: '1px solid rgba(127,127,127,.35)', my: 0.5 }} />
          <TipRow color={colors.failed} round label="Failed (custom)" value={fmt(hovered.customFailed)} />
          <TipRow
            label="Success (custom)"
            value={hovered.customSent ? `${(((hovered.customSent - hovered.customFailed) / hovered.customSent) * 100).toFixed(1)}%` : '—'}
          />
          <TipRow label="No recipients" value={fmt(hovered.customNoRecipients)} />
          <Box sx={{ opacity: 0.7, fontSize: 11, mt: 0.25 }}>
            {totalOf(hovered) === 0 ? 'No pushes sent this day.' : '*first send per customer, no failure data'}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const TipRow: React.FC<{ label: string; value: string; color?: string; round?: boolean }> = ({ label, value, color, round }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontVariantNumeric: 'tabular-nums' }}>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {color && <Box sx={{ width: 10, height: 10, borderRadius: round ? '50%' : '2px', bgcolor: color }} />}
      {label}
    </Box>
    <b>{value}</b>
  </Box>
);

export default PushStatsChart;
